import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import {
	ADD_ATELIER_HEURE,
	DELETE_ATELIER_HEURE,
	getBaseHeure,
	HEURE_TYPE_ATELIER,
	UPDATE_ATELIER_HEURE,
} from "../../../store/actions/heureActions"
import { typeNumber, dateFrToISO, dateISOToFr } from "../../../utilities/Addons"
import InputV2 from "../../basics/input/InputV2"
import { generalStyles } from "../../styles/styles"
import { COLORS } from "../../../utilities/Constants"
import { FieldColumn } from "../../cards/EntiteCards"
// Heures de travail = attribution au groupe employe (même règle que le
// mécanicien de la fiche et que feu ModalHeures).
import { filterEmployesGroupe } from "../../../utilities/employes"

// Une ligne d'heure (module-level → refs stables par ligne, pas de hooks dans un
// map). Chaîne de focus Entrée : Description → Date → Heures (Maj+Entrée recule).
// Le select Employé reste hors chaîne (comme les selects des lignes de travaux).
const HeureRow = ({
	heure,
	index,
	isDisabled,
	depanneurs,
	updateHeure,
	deleteHeure,
	autoFocus,
	onAutoFocused,
}) => {
	const refDesc = useRef(null)
	const refDate = useRef(null)
	const refHeures = useRef(null)
	// Focus du 1er champ à l'ajout (clic « Ajouter » → saisie clavier directe).
	useEffect(() => {
		if (autoFocus && refDesc.current) {
			refDesc.current.focus()
			if (onAutoFocused) onAutoFocused()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	return (
		<div
			style={{
				display: "flex",
				alignItems: "flex-end",
				gap: 8,
				padding: "8px 0",
				borderBottom: `1px solid ${COLORS.border}`,
			}}
		>
			<FieldColumn
				label={index === 0 ? "Description" : ""}
				style={{ flex: 3 }}
			>
				<InputV2
					ref={refDesc}
					nextRef={refDate}
					type="text"
					style={generalStyles.input}
					value={heure.description}
					disabled={isDisabled}
					onChange={(e) =>
						updateHeure("description", e.target.value, index)
					}
				/>
			</FieldColumn>
			<FieldColumn label={index === 0 ? "Date" : ""} style={{ width: 120 }}>
				<InputV2
					ref={refDate}
					nextRef={refHeures}
					prevRef={refDesc}
					type="date"
					style={generalStyles.input}
					value={dateFrToISO(heure.date)}
					disabled={isDisabled}
					onChange={(e) =>
						updateHeure("date", dateISOToFr(e.target.value), index)
					}
				/>
			</FieldColumn>
			<FieldColumn label={index === 0 ? "Heures" : ""} style={{ width: 80 }}>
				<InputV2
					ref={refHeures}
					prevRef={refDate}
					type="text"
					style={{ ...generalStyles.input, textAlign: "right" }}
					value={heure.heures}
					disabled={isDisabled}
					onChange={(e) => updateHeure("heures", e.target.value, index)}
					onKeyDown={(e) => typeNumber(e)}
				/>
			</FieldColumn>
			<FieldColumn label={index === 0 ? "Employé" : ""} style={{ width: 150 }}>
				<select
					style={generalStyles.select}
					value={heure.employe}
					disabled={isDisabled}
					onChange={(e) =>
						updateHeure("employe", parseInt(e.target.value), index)
					}
				>
					<option value={0}>-</option>
					{filterEmployesGroupe(depanneurs, heure.employe).map((dep) => (
						<option key={`heure_emp_${dep.id}_${index}`} value={dep.id}>
							{dep.nom}
						</option>
					))}
				</select>
			</FieldColumn>
			{!isDisabled && (
				<button
					type="button"
					title="Supprimer"
					style={generalStyles.iconBtn(COLORS.danger)}
					onClick={() => deleteHeure(index)}
				>
					<FontAwesomeIcon icon={faTrash} />
				</button>
			)}
		</div>
	)
}

/**
 * Heures de travail — rendu V2 (remplace le legacy AtelierFicheHeures).
 *
 * La visibilité (droits + état) et l'enveloppe repliable sont gérées par le
 * parent (AtelierV2). Ici on rend les lignes + l'ajout. Les heures restent
 * gérées via redux (atelierInfo.heures) comme dans le legacy.
 */
const AtelierFicheHeuresV2 = ({ isDisabled }) => {
	const dispatch = useDispatch()
	const { employes: depanneurs } = useSelector((state) => state.masterReducer)
	const { heures, atelier } = useSelector((state) => state.atelierInfo)
	const [autoFocusIdx, setAutoFocusIdx] = useState(null)

	const addHeure = () => {
		const newHeure = getBaseHeure(HEURE_TYPE_ATELIER)
		if (heures.length === 0) newHeure.employe = atelier.employe
		dispatch({ type: ADD_ATELIER_HEURE, value: newHeure })
		setAutoFocusIdx(heures.length) // la nouvelle ligne sera à cet index
	}
	const updateHeure = (key, value, index) => {
		dispatch({
			type: UPDATE_ATELIER_HEURE,
			value: { ...heures[index], [key]: value },
			index,
		})
	}
	const deleteHeure = (index) => {
		dispatch({ type: DELETE_ATELIER_HEURE, index })
	}

	return (
		<div>
			{heures.length === 0 && (
				<div style={{ fontSize: 13, color: COLORS.textMuted }}>
					Aucune heure saisie.
				</div>
			)}
			{heures.map((heure, index) => (
				<HeureRow
					key={`heure_${heure.id}_${index}`}
					heure={heure}
					index={index}
					isDisabled={isDisabled}
					depanneurs={depanneurs}
					updateHeure={updateHeure}
					deleteHeure={deleteHeure}
					autoFocus={index === autoFocusIdx}
					onAutoFocused={() => setAutoFocusIdx(null)}
				/>
			))}
			{!isDisabled && (
				<button
					type="button"
					style={{ ...generalStyles.addBtn, marginTop: 10 }}
					onClick={addHeure}
				>
					<FontAwesomeIcon icon={faPlus} />
					Ajouter une heure
				</button>
			)}
		</div>
	)
}

export default AtelierFicheHeuresV2
